/**
 * 🔐 GERENCIADOR SEGURO DE TOKENS JWT
 * Sistema avançado de gestão de tokens com validação e rotação automática
 */

import { refreshSession, SessionRefreshError } from './refreshSession';

export interface TokenData {
  token: string;
  timestamp: number;
  expiresAt: number;
  refreshCount: number;
  deviceId: string;
}

export interface TokenValidationResult {
  valid: boolean;
  expired: boolean;
  remainingTime?: number;
  shouldRefresh?: boolean;
  error?: string;
}

export interface RefreshResult {
  success: boolean;
  newToken?: string;
  newRefreshToken?: string;
  error?: string;
}

export class SecureTokenManager {
  private static instance: SecureTokenManager;
  private readonly storageKeys = {
    accessToken: 'padelmaster_access_token',
    refreshToken: 'padelmaster_refresh_token',
    tokenData: 'padelmaster_token_data',
    deviceId: 'padelmaster_device_id'
  };
  
  private readonly refreshThreshold = 5 * 60 * 1000; // 5 minutos antes da expiração
  private refreshTimer?: NodeJS.Timeout;

  private constructor() {
    this.initializeDeviceId();
    this.startTokenMonitoring();
  }

  static getInstance(): SecureTokenManager {
    if (!SecureTokenManager.instance) {
      SecureTokenManager.instance = new SecureTokenManager();
    }
    return SecureTokenManager.instance;
  }

  /**
   * Armazenar tokens de forma segura
   */
  setTokens(accessToken: string, refreshToken?: string): boolean {
    try {
      // Validar formato dos tokens
      if (!this.validateTokenFormat(accessToken)) {
        console.error('❌ Formato de access token inválido');
        return false;
      }

      if (refreshToken && !this.validateTokenFormat(refreshToken)) {
        console.error('❌ Formato de refresh token inválido');
        return false;
      }

      // Extrair informações do token
      const tokenInfo = this.parseJWTPayload(accessToken);
      if (!tokenInfo) {
        console.error('❌ Não foi possível parsear o token');
        return false;
      }

      const now = Date.now();
      const expiresAt = tokenInfo.exp && typeof tokenInfo.exp === 'number' 
        ? tokenInfo.exp * 1000 
        : now + (60 * 60 * 1000); // 1 hora default

      // Verificar se o token não está expirado
      if (expiresAt <= now) {
        console.error('❌ Token já está expirado');
        return false;
      }

      // Dados do token para armazenamento
      const tokenData: TokenData = {
        token: accessToken,
        timestamp: now,
        expiresAt,
        refreshCount: 0,
        deviceId: this.getDeviceId()
      };

      // Armazenar tokens
      localStorage.setItem(this.storageKeys.accessToken, accessToken);
      localStorage.setItem(this.storageKeys.tokenData, JSON.stringify(tokenData));

      if (refreshToken) {
        localStorage.setItem(this.storageKeys.refreshToken, refreshToken);
      }

      // Agendar refresh automático
      this.scheduleTokenRefresh(expiresAt - now - this.refreshThreshold);

      console.log('✅ Tokens armazenados com segurança', {
        expiresIn: Math.round((expiresAt - now) / 1000),
        userId: tokenInfo.user_id || 'unknown'
      });

      return true;

    } catch (error) {
      console.error('❌ Erro ao armazenar tokens:', error);
      return false;
    }
  }

  /**
   * Obter access token atual
   */
  getAccessToken(): string | null {
    try {
      // O login (email e Google) grava nas chaves legadas 'accessToken'/
      // 'refreshToken' — sem este fallback, o tokenManager nunca via os
      // tokens de quem entrou pelo fluxo normal e a app parecia deslogada.
      const token =
        localStorage.getItem(this.storageKeys.accessToken) ||
        localStorage.getItem('accessToken') ||
        localStorage.getItem('access_token');
      if (!token) return null;

      // Validar se o token ainda é válido
      const validation = this.validateToken(token);
      // Expirar o access não expira a sessão: o refresh ainda pode renová-la.
      if (validation.expired) return token;
      if (!validation.valid) {
        console.warn('⚠️ Token inválido encontrado, removendo...');
        this.clearTokens();
        return null;
      }

      // Verificar se precisa ser atualizado
      if (validation.shouldRefresh) {
        console.log('🔄 Token precisa ser atualizado');
        this.refreshTokenIfNeeded();
      }

      return token;

    } catch (error) {
      console.error('❌ Erro ao obter access token:', error);
      return null;
    }
  }

  /**
   * Obter refresh token atual
   */
  getRefreshToken(): string | null {
    return (
      localStorage.getItem(this.storageKeys.refreshToken) ||
      localStorage.getItem('refreshToken') ||
      localStorage.getItem('refresh_token')
    );
  }

  /**
   * Validar token atual
   */
  validateToken(token?: string): TokenValidationResult {
    const tokenToValidate = token || this.getStoredAccessToken();
    
    if (!tokenToValidate) {
      return { valid: false, expired: true, error: 'Token não encontrado' };
    }

    // Validar formato
    if (!this.validateTokenFormat(tokenToValidate)) {
      return { valid: false, expired: false, error: 'Formato de token inválido' };
    }

    // Parsear payload
    const payload = this.parseJWTPayload(tokenToValidate);
    if (!payload || !payload.exp || typeof payload.exp !== 'number') {
      return { valid: false, expired: false, error: 'Payload de token inválido' };
    }

    const now = Date.now();
    const expiresAt = payload.exp * 1000;
    const remainingTime = expiresAt - now;

    // Verificar expiração
    if (remainingTime <= 0) {
      return { valid: false, expired: true, remainingTime: 0 };
    }

    // Verificar se deve ser renovado
    const shouldRefresh = remainingTime <= this.refreshThreshold;

    return {
      valid: true,
      expired: false,
      remainingTime,
      shouldRefresh
    };
  }

  /**
   * Atualizar token automaticamente
   */
  async refreshTokenIfNeeded(): Promise<RefreshResult> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return { success: false, error: 'Refresh token não disponível' };
    }

    try {
      const data = await refreshSession();

      if (data.access && this.setTokens(data.access, data.refresh)) {
        // Atualizar contador de refresh
        this.incrementRefreshCount();
        
        console.log('✅ Token atualizado com sucesso');
        return { 
          success: true, 
          newToken: data.access, 
          newRefreshToken: data.refresh 
        };
      } else {
        throw new Error('Resposta de refresh inválida');
      }

    } catch (error) {
      console.error('❌ Erro ao atualizar token:', error);
      
      // Uma falha de rede não termina uma sessão ainda recuperável.
      if (error instanceof SessionRefreshError && error.expired) this.clearTokens();
      
      return { success: false, error: error instanceof Error ? error.message : 'Erro desconhecido' };
    }
  }

  /**
   * Verificar se o usuário está autenticado
   */
  isAuthenticated(): boolean {
    const token = this.getStoredAccessToken();
    if (!token) return false;

    const validation = this.validateToken(token);
    return validation.valid && !validation.expired;
  }

  /**
   * Limpar todos os tokens
   */
  clearTokens(): void {
    try {
      localStorage.removeItem(this.storageKeys.accessToken);
      localStorage.removeItem(this.storageKeys.refreshToken);
      localStorage.removeItem(this.storageKeys.tokenData);

      // Limpar timer de refresh
      if (this.refreshTimer) {
        clearTimeout(this.refreshTimer);
        this.refreshTimer = undefined;
      }

      console.log('🗑️ Tokens limpos');

    } catch (error) {
      console.error('❌ Erro ao limpar tokens:', error);
    }
  }

  /**
   * Obter informações do token atual
   */
  getTokenInfo(): TokenData | null {
    try {
      const tokenDataStr = localStorage.getItem(this.storageKeys.tokenData);
      if (!tokenDataStr) return null;

      const tokenData: TokenData = JSON.parse(tokenDataStr);
      
      // Validar estrutura
      if (!tokenData.token || !tokenData.timestamp || !tokenData.expiresAt) {
        return null;
      }

      return tokenData;

    } catch (error) {
      console.error('❌ Erro ao obter informações do token:', error);
      return null;
    }
  }

  /**
   * Validar formato JWT
   */
  private validateTokenFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false;

    const parts = token.split('.');
    if (parts.length !== 3) return false;

    // Verificar se cada parte é base64url válida
    const base64urlRegex = /^[A-Za-z0-9_-]+$/;
    return parts.every(part => base64urlRegex.test(part));
  }

  /**
   * Parsear payload do JWT
   */
  private parseJWTPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      // Decodificar payload (segunda parte)
      const payload = parts[1];
      // Adicionar padding se necessário
      const paddedPayload = payload.padEnd(payload.length + (4 - payload.length % 4) % 4, '=');
      
      const decoded = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);

    } catch (error) {
      console.error('❌ Erro ao parsear JWT payload:', error);
      return null;
    }
  }

  /**
   * Obter token armazenado sem validação
   */
  private getStoredAccessToken(): string | null {
    return localStorage.getItem(this.storageKeys.accessToken);
  }

  /**
   * Inicializar ID do dispositivo
   */
  private initializeDeviceId(): void {
    let deviceId = localStorage.getItem(this.storageKeys.deviceId);
    
    if (!deviceId) {
      deviceId = this.generateDeviceId();
      localStorage.setItem(this.storageKeys.deviceId, deviceId);
    }
  }

  /**
   * Obter ID do dispositivo
   */
  private getDeviceId(): string {
    return localStorage.getItem(this.storageKeys.deviceId) || 'unknown';
  }

  /**
   * Gerar ID único do dispositivo
   */
  private generateDeviceId(): string {
    const navigator_info = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    const device_string = JSON.stringify(navigator_info);
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substring(2, 15);
    
    return btoa(`${device_string}_${timestamp}_${random}`).substring(0, 32);
  }

  /**
   * Agendar refresh automático do token
   */
  private scheduleTokenRefresh(delayMs: number): void {
    // Limpar timer anterior
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
    }

    // Agendar novo refresh se delay for positivo
    if (delayMs > 0) {
      this.refreshTimer = setTimeout(() => {
        console.log('⏰ Iniciando refresh automático do token');
        this.refreshTokenIfNeeded();
      }, Math.min(delayMs, 2147483647)); // Máximo valor para setTimeout
    }
  }

  /**
   * Incrementar contador de refresh
   */
  private incrementRefreshCount(): void {
    const tokenData = this.getTokenInfo();
    if (tokenData) {
      tokenData.refreshCount++;
      localStorage.setItem(this.storageKeys.tokenData, JSON.stringify(tokenData));
    }
  }

  /**
   * Iniciar monitoramento contínuo de tokens
   */
  private startTokenMonitoring(): void {
    // Verificar tokens a cada minuto
    setInterval(() => {
      const tokenData = this.getTokenInfo();
      if (tokenData) {
        const validation = this.validateToken(tokenData.token);
        
        if (!validation.valid && validation.expired) {
          console.warn('⚠️ Token expirado detectado, limpando...');
          this.clearTokens();
        } else if (validation.shouldRefresh) {
          console.log('🔄 Token precisa de refresh - iniciando...');
          this.refreshTokenIfNeeded();
        }
      }
    }, 60000); // 1 minuto
  }
}

// Instância singleton exportada
export const tokenManager = SecureTokenManager.getInstance();
